/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionInvokeTrigger';

const ActionInvokeTriggerManager: ActionManagerFn<'ActionInvokeTrigger'> = (
  platform: Platform,
): ActionManager<'ActionInvokeTrigger'> => {
  return {
    ...baseActionManager(platform, 'ActionInvokeTrigger'),
    defaultValue,
    props: {
      ...baseActionProps,
      targetItemId: {
        type: 'ItemID',
        label: 'Target Item Id',
        initialFilter: 'Trigger',
      },
    },
    name: 'Invoke Trigger',
    getDisplayText: (item) => item.props.targetItemId,
    icon: { iconName: 'play' },
    description: `**Overview**<br/>
The Invoke Trigger action is a logic flow management component designed to programmatically execute a sequence of actions defined elsewhere on the page. It functions as a subroutine call, allowing developers to reuse the logic already attached to another component's event (such as a button click or page load) without asking the user to manually interact with that specific component.<br/><br/>
This action enables the "Don't Repeat Yourself" (DRY) principle within the application builder. Instead of duplicating a complex chain of 10 actions for multiple different scenarios, a developer can define them once under a specific trigger and then simply use this action to "call" that trigger whenever needed.

**Key Features**<br/>
* **Logic Reusability:** Triggers existing action chains defined on other components, centralizing logic in one place while allowing it to be invoked from multiple sources.
* **Targeted Execution:** Uses an ItemID property to pinpoint the specific trigger to execute.
* **Registry Filtering:** The property picker uses initialFilter: 'Trigger', streamlining the configuration by showing relevant triggerable items.
* **Visual Context:** The editor displays the targetItemId in the action list (getDisplayText), allowing developers to easily see which routine is being called.

**Properties**<br/>
* **Target Item Id (targetItemId):** A property of type ItemID.
  * *Function:* Identifies the specific trigger (e.g., Button1.onClick, Page.onLoad) whose actions should be executed.
  * *Filter:* Restricted to items classified as Triggers.

**Use Cases**<br/>
* **Simulated Interactions:** Programmatically "clicking" a Save button (triggering its validation and save logic) when the user presses "Enter" in a text field.
* **Shared Logic:** Defining a complex data processing workflow on a hidden component's trigger and calling it from both a specific button and a timer event.
* **Initialization:** Manually re-running the Page.onLoad logic to refresh the screen without reloading the entire browser page.`,
  };
};

export default ActionInvokeTriggerManager;
